/* PrintNodeSettings — Settings sayfasında PrintNode yapılandırma kartı.
 *
 * - API key input (masked once saved)
 * - Doğrulama: backend whoami çağırır
 * - Default printer seçimi
 * - "Bağlantıyı Kaldır" butonu
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Printer, Loader2, CheckCircle2, AlertCircle, Trash2, ExternalLink } from 'lucide-react';
import {
    usePrintNodeConfig,
    usePrintNodePrinters,
    useSavePrintNodeConfig,
    useDeletePrintNodeConfig,
} from '../../../hooks/usePrintNode';

export default function PrintNodeSettings() {
    const cfgQuery = usePrintNodeConfig();
    const cfg = cfgQuery.data;
    const configured = !!cfg?.configured;

    const printersQuery = usePrintNodePrinters({ enabled: configured });
    const saveMut = useSavePrintNodeConfig();
    const deleteMut = useDeletePrintNodeConfig();

    const [apiKey, setApiKey] = useState('');
    const [defaultPrinterId, setDefaultPrinterId] = useState(null);

    useEffect(() => {
        if (cfg?.default_printer_id) setDefaultPrinterId(cfg.default_printer_id);
    }, [cfg?.default_printer_id]);

    const handleSave = async () => {
        if (!apiKey && !configured) {
            toast.error('API key gir');
            return;
        }
        try {
            const defaultPrinter = (printersQuery.data?.printers || []).find((p) => p.id === defaultPrinterId);
            await saveMut.mutateAsync({
                apiKey: apiKey || undefined,
                defaultPrinterId,
                defaultPrinterName: defaultPrinter?.name,
            });
            toast.success('PrintNode yapılandırıldı');
            setApiKey('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydedilemedi');
        }
    };

    const handleSaveOnlyDefault = async () => {
        // API key zaten kayıtlı, sadece default printer'ı güncelle
        try {
            const defaultPrinter = (printersQuery.data?.printers || []).find((p) => p.id === defaultPrinterId);
            // Backend için api_key gerekli (validation) — masked'i değil, kaydedilmiş key'i kullanmak için
            // endpoint'i sadece default güncellemesi için patch yapalım
            const token = localStorage.getItem('token');
            const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
            // Burada sadece save mutation'ı çağırmıyoruz — direct PATCH-like:
            // saveMut zaten api_key gerektiriyor. Bu yüzden config'i tek seferde silmeden update etmek için
            // save endpoint'ine YENİ api_key + defaultPrinter göndermeliyiz.
            // Pratik çözüm: kullanıcı default'ı değiştirmek için API key'i tekrar yazmak zorunda.
            toast.info('Varsayılan yazıcıyı değiştirmek için API key\'ini tekrar gir');
            // eslint-disable-next-line no-unused-vars
            const _ = { token, API, defaultPrinter };
        } catch (e) {
            toast.error('Hata');
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('PrintNode bağlantısını kaldır?')) return;
        try {
            await deleteMut.mutateAsync();
            toast.success('PrintNode bağlantısı kaldırıldı');
        } catch (e) {
            toast.error('Hata');
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 max-w-2xl">
            <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center shrink-0">
                    <Printer size={18} className="text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900">PrintNode — Uzaktan Yazdırma</h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Fabrikadaki yazıcıya internetten yazdır. Tatildeyken telefondan basabilirsin.
                    </p>
                </div>
                {configured && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200">
                        <CheckCircle2 size={11} /> Bağlı
                    </span>
                )}
            </div>

            {cfgQuery.isLoading ? (
                <div className="text-center py-6 text-gray-500"><Loader2 className="animate-spin mx-auto" size={20} /></div>
            ) : !configured ? (
                <div className="space-y-3">
                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-xs text-cyan-900 leading-relaxed">
                        <b>Kurulum:</b><br />
                        1. <a href="https://app.printnode.com" target="_blank" rel="noreferrer" className="underline font-semibold">app.printnode.com</a> aç, hesap aç/giriş yap<br />
                        2. <b>Account → API Keys → Create</b> ile yeni key oluştur<br />
                        3. Fabrikadaki PC'ye <a href="https://www.printnode.com/download" target="_blank" rel="noreferrer" className="underline font-semibold">PrintNode Client</a> kur (5 dk, Windows/Mac/Linux)<br />
                        4. API key'i aşağıya yapıştır
                    </div>
                    <label className="block">
                        <span className="text-xs font-semibold text-gray-700 mb-1 block">PrintNode API Key</span>
                        <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder="örn: u8gJWXV5yUgTF9yLhfV..."
                            className="w-full h-10 px-3 text-sm font-mono border border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                            data-testid="printnode-api-key-input" />
                    </label>
                    <button onClick={handleSave}
                        disabled={!apiKey || saveMut.isPending}
                        className="w-full h-10 text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 rounded-lg flex items-center justify-center gap-2"
                        data-testid="printnode-save-btn">
                        {saveMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        Bağla ve Doğrula
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-xs">
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <div className="text-gray-500">API Key</div>
                                <div className="font-mono text-gray-900">{cfg.api_key_masked}</div>
                            </div>
                            <button onClick={handleDisconnect}
                                disabled={deleteMut.isPending}
                                className="h-8 px-2 text-[11px] font-semibold text-rose-700 bg-white border border-rose-300 hover:bg-rose-50 rounded-md flex items-center gap-1"
                                data-testid="printnode-disconnect-btn">
                                <Trash2 size={11} /> Kaldır
                            </button>
                        </div>
                    </div>

                    {/* Yazıcı listesi */}
                    <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1.5">Varsayılan Yazıcı</div>
                        {printersQuery.isLoading ? (
                            <div className="text-center py-4"><Loader2 className="animate-spin mx-auto text-gray-400" size={18} /></div>
                        ) : (printersQuery.data?.printers || []).length === 0 ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                                <AlertCircle size={12} className="inline mr-1" />
                                Yazıcı yok. PrintNode Client fabrikadaki PC'de açık mı?
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {(printersQuery.data?.printers || []).map((p) => (
                                    <label key={p.id}
                                        className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border cursor-pointer text-xs ${
                                            defaultPrinterId === p.id ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
                                        }`}>
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <input type="radio" name="default-printer"
                                                checked={defaultPrinterId === p.id}
                                                onChange={() => setDefaultPrinterId(p.id)} />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-gray-900 truncate">{p.name}</div>
                                                <div className="text-[10px] text-gray-500 truncate">
                                                    {p.computer_name && `${p.computer_name} · `}
                                                    <span className={p.state === 'online' ? 'text-emerald-700 font-semibold' : 'text-rose-700'}>{p.state}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {defaultPrinterId !== cfg.default_printer_id && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="text-xs text-amber-900 mb-2">
                                Varsayılan yazıcıyı değiştirmek için API key&apos;ini tekrar gir:
                            </div>
                            <div className="flex gap-2">
                                <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="PrintNode API key"
                                    className="flex-1 h-9 px-2 text-xs font-mono border border-amber-300 rounded" />
                                <button onClick={handleSave}
                                    disabled={!apiKey || saveMut.isPending}
                                    className="h-9 px-3 text-xs font-semibold text-white bg-amber-700 hover:bg-amber-800 disabled:opacity-50 rounded">
                                    {saveMut.isPending ? <Loader2 size={11} className="animate-spin" /> : 'Kaydet'}
                                </button>
                            </div>
                        </div>
                    )}

                    <a href="https://app.printnode.com" target="_blank" rel="noreferrer"
                        className="text-[11px] text-cyan-700 hover:underline inline-flex items-center gap-1">
                        PrintNode Dashboard'ı aç <ExternalLink size={10} />
                    </a>
                </div>
            )}
        </div>
    );
}
